import { useState, useEffect, useRef } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Stack,
    Divider,
    Paper,
    Chip
} from "@mui/material";
import { TwoFactorStatus, TwoFactorSetup } from "./models";
import { toast } from "sonner";
import Disable2FADialog from "./Disable2FADialog";
import ResetAuthenticatorDialog from "./ResetAuthenticatorDialog";
import GenerateRecoveryCodesDialog from "./GenerateRecoveryCodesDialog";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";

type Props = {
    status: TwoFactorStatus | null;
    setup: TwoFactorSetup | null;
    recoveryCodes: string[] | null;
    loading: boolean;
    error: string | null;
    actionMessage: string | null;
    beginSetup: () => void;
    enable: (code: string) => void;
    generateRecoveryCodes: () => void;
    disable: () => void;
    reset: () => void;
    clearMessages: () => void;
};

let qrScriptPromise: Promise<void> | null = null;
function ensureQrScript(): Promise<void> {
    if (typeof window !== "undefined" && (window as any).QRCode) {
        return Promise.resolve();
    }
    if (qrScriptPromise) return qrScriptPromise;

    qrScriptPromise = new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>('script[data-origin="qrcode-lib"]');
        if (existing) {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", () => reject(new Error("QR script load failed")), { once: true });
            return;
        }
        const script = document.createElement("script");
        script.src = "/lib/qrcode/qrcode.min.js";
        script.dataset.origin = "qrcode-lib";
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("QR script load failed"));
        document.head.appendChild(script);
    });

    return qrScriptPromise;
}

export default function SecurityPresenter({
    status,
    setup,
    recoveryCodes,
    loading,
    error,
    actionMessage,
    beginSetup,
    enable,
    generateRecoveryCodes,
    disable,
    reset,
    clearMessages
}: Props) {
    const [verifyCode, setVerifyCode] = useState("");
    const [qrLoading, setQrLoading] = useState(false);
    const [qrError, setQrError] = useState<string | null>(null);
    const [disableOpen, setDisableOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [generateOpen, setGenerateOpen] = useState(false);


    const qrRef = useRef<HTMLDivElement | null>(null);
    const enabled = status?.enabled;

    useEffect(() => {
        let cancelled = false;
        async function renderQr() {
            if (!setup || enabled) return;
            if (!qrRef.current) return;
            setQrError(null);
            setQrLoading(true);
            try {
                await ensureQrScript();
                if (cancelled) return;
                const QRCodeCtor = (window as any).QRCode;
                if (!QRCodeCtor) throw new Error("QRCode constructor not found after script load.");
                qrRef.current.innerHTML = "";
                new QRCodeCtor(qrRef.current, {
                    text: setup.otpauthUri,
                    width: 180,
                    height: 180,
                    correctLevel: (window as any).QRCode?.CorrectLevel?.M
                });
            } catch (e: any) {
                if (!cancelled) setQrError(e?.message || "Failed to generate QR");
            } finally {
                if (!cancelled) setQrLoading(false);
            }
        }
        renderQr();
        return () => { cancelled = true; };
    }, [setup, enabled]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            clearMessages();
        }
    }, [error, clearMessages]);

    useEffect(() => {
        if (actionMessage) {
            toast.success(actionMessage);
            clearMessages();
        }
    }, [actionMessage, clearMessages]);

    return (
        <Box sx={styles.container}>
            <Paper sx={styles.paper}>
                <Stack spacing={1}>
                    <Typography variant="h6">Two-Factor Authentication</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Protect your account by requiring a 6-digit code from an authenticator app
                        (e.g., Google Authenticator, Microsoft Authenticator) when signing in.
                    </Typography>
                    <Divider sx={styles.divider} />
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>Status:</Typography>
                        {status && (
                            <Chip
                                size="small"
                                label={enabled ? "Enabled" : "Disabled"}
                                color={enabled ? "success" : "error"}
                                variant="filled"
                            />
                        )}
                    </Stack>
                    {enabled && (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                Remaining recovery codes: {status?.remainingRecoveryCodes}
                            </Typography>
                            <Stack direction="row" spacing={2} sx={styles.actionsRow}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    disabled={loading}
                                    onClick={() => setDisableOpen(true)}
                                >
                                    Disable 2FA
                                </Button>
                                <Button
                                    variant="outlined"
                                    disabled={loading}
                                    onClick={() => setResetOpen(true)}
                                >
                                    Reset Authenticator
                                </Button>
                            </Stack>
                        </>
                    )}
                    {!enabled && !setup && (
                        <Button variant="contained" onClick={beginSetup} disabled={loading} sx={styles.enableBtn}>
                            Enable 2FA
                        </Button>
                    )}
                </Stack>
            </Paper>

            {!enabled && setup && (
                <Paper sx={styles.paper}>
                    <Stack spacing={2}>
                        <Typography variant="h6">Set Up Authenticator</Typography>
                        <Typography variant="body2">
                            1. Scan the QR code below with your authenticator app or enter the key manually.
                        </Typography>
                        <Box sx={styles.qrRow}>
                            <Box sx={styles.qrBox}>
                                <Box ref={qrRef} sx={styles.qrCanvas} />
                                {qrError && (
                                    <Typography variant="caption" color="error" sx={styles.qrError}>
                                        {qrError}
                                    </Typography>
                                )}
                            </Box>
                            <Stack spacing={1} sx={styles.keyColumn}>
                                <Typography variant="body2" sx={styles.sharedKey}>{setup.sharedKey}</Typography>
                                <Typography variant="caption" color="text.secondary">(Keep this key secret)</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    If you cannot scan, add an account manually in your app and paste this key.
                                </Typography>
                            </Stack>
                        </Box>
                        <Typography variant="body2">
                            2. Enter the 6-digit code generated by the app to verify.
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                label="Verification Code"
                                value={verifyCode}
                                onChange={(e) => setVerifyCode(e.target.value)}
                                size="small"
                                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                            />
                            <Button
                                variant="contained"
                                disabled={loading || verifyCode.length < 6}
                                onClick={() => enable(verifyCode)}
                            >
                                Verify & Enable
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            {enabled && (
                <Paper sx={styles.paper}>
                    <Stack spacing={2}>
                        <Typography variant="h6">Recovery Codes</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Each recovery code can be used once if you lose access to your authenticator device.
                            Store them securely (password manager or print & keep safe).
                        </Typography>
                        {recoveryCodes && (
                            <Box component="pre" sx={styles.codesPre}>
                                {recoveryCodes.join("\n")}
                            </Box>
                        )}
                        <Stack direction="row" spacing={2}>
                            <Button variant="outlined" onClick={() => setGenerateOpen(true)} disabled={loading}>
                                Generate New Codes
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            <Disable2FADialog
                open={disableOpen}
                onClose={(confirmed) => {
                    setDisableOpen(false);
                    if (confirmed) disable();
                }}
            />
            <ResetAuthenticatorDialog
                open={resetOpen}
                onClose={(confirmed: any) => {
                    setResetOpen(false);
                    if (confirmed) reset();
                }}
            />
            <GenerateRecoveryCodesDialog
                open={generateOpen}
                onClose={(confirmed) => {
                    setGenerateOpen(false);
                    if (confirmed) generateRecoveryCodes();
                }}
            />
        </Box>
    );
}

const styles = {
    container: {
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3
    },
    paper: {
        p: 3,
        boxShadow: "none"
    },
    divider: {
        my: 1
    },
    enableBtn: {
        mt: 1,
        alignSelf: "flex-start"
    },
    qrRow: {
        display: "flex",
        gap: 4,
        flexWrap: "wrap"
    },
    qrBox: {
        width: 180,
        height: 180,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "background.default",
        borderRadius: 1
    },
    qrCanvas: {
        width: 180,
        height: 180,
        "& canvas": { width: "100% !important", height: "100% !important" }
    },
    qrError: {
        position: "absolute",
        bottom: 4,
        left: 4,
        right: 4,
        textAlign: "center"
    },
    keyColumn: {
        minWidth: 200
    },
    sharedKey: {
        fontFamily: "monospace",
        wordBreak: "break-all"
    },
    codesPre: {
        backgroundColor: "background.default",
        p: 2,
        borderRadius: 1,
        fontFamily: "monospace",
        fontSize: 14,
        maxWidth: 300,
        whiteSpace: "pre-wrap"
    },
    actionsRow: {
        mt: 2
    }
};
